/* ============================================================================
   FRÈRES — Journal
   ----------------------------------------------------------------------------
   Hidrata los módulos interactivos de una nota. Solo carga en el Journal
   (lo emite sections/freres-journal-article.liquid, no theme.liquid).

   TRES REGLAS QUE VIENEN DEL RESTO DEL THEME, NO SON CAPRICHO:

   1. freres-header.js observa document.body con MutationObserver sobre
      'style' y 'class', subtree, SIN debounce, y en cada disparo reescribe
      estilos inline de todo el header. O sea: cualquier mutación de style o
      class dentro de <body> cuesta un barrido completo del header.
      → Los valores continuos (progreso de lectura) se escriben en
        document.documentElement, que está FUERA del subtree observado.
      → Los contadores mutan textNode.nodeValue (characterData), nunca
        textContent (childList, sí observado).
      → Los estados discretos van por clase, pero disparados por
        IntersectionObserver: unas pocas mutaciones por nota, no por frame.

   2. El estado base del CSS es el estado sin JS, y es legible. El atributo
      data-fr-jr-ready se pone en <html> al FINAL de un init exitoso.
      Nunca ocultar-por-defecto + mostrar-con-JS.

   3. prefers-reduced-motion corta todo antes de poner el gate.
   ============================================================================ */

(function () {
  'use strict';

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --- utilidades ---------------------------------------------------------- */

  function all(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  function clamp01(n) {
    return n < 0 ? 0 : n > 1 ? 1 : n;
  }

  /* --- 1. Barra de progreso de lectura -------------------------------------
     Mide el CUERPO de la nota, no el documento: si midiera el documento, el
     footer y los productos del pie desvirtúan el 100%.
     El listener de scroll solo existe mientras el cuerpo está en pantalla. */

  function initProgress(body) {
    var ticking = false;

    function tick() {
      ticking = false;
      var r = body.getBoundingClientRect();
      var scrollable = r.height - window.innerHeight;
      var p = scrollable > 0 ? -r.top / scrollable : r.top <= 0 ? 1 : 0;
      document.documentElement.style.setProperty('--fr-jr-read', clamp01(p).toFixed(3));
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(tick);
    }

    var gate = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            window.addEventListener('scroll', onScroll, { passive: true });
            tick();
          } else {
            window.removeEventListener('scroll', onScroll);
          }
        });
      },
      { threshold: 0 }
    );
    gate.observe(body);
  }

  /* --- 2. Scrolly: foto fija + pasos que la cambian ------------------------- */

  function showImage(img) {
    if (!img || img.classList.contains('is-on')) return;
    var sibs = img.parentNode.children;
    for (var i = 0; i < sibs.length; i++) {
      if (sibs[i] !== img) sibs[i].classList.remove('is-on');
    }
    img.classList.add('is-on');
  }

  function markStep(step) {
    if (!step || step.classList.contains('is-on')) return;
    var sibs = step.parentNode.children;
    for (var i = 0; i < sibs.length; i++) {
      if (sibs[i] !== step) sibs[i].classList.remove('is-on');
    }
    step.classList.add('is-on');
  }

  function initScrolly(sc, io) {
    var media = sc.querySelector('.fr-jr-scrolly__media');
    var steps = all(sc, '.fr-jr-scrolly__step');
    var imgs = media ? all(media, 'img') : [];

    // Con menos de 2 fotos o sin pasos no hay nada que contar: se deja apilado.
    if (!media || imgs.length < 2 || !steps.length) return false;

    // La primera arranca visible ANTES de que se ponga el gate, así el bloque
    // nunca puede quedar en negro.
    imgs[0].classList.add('is-on');
    steps[0].classList.add('is-on');

    steps.forEach(function (step, i) {
      step._frJrImg = imgs[i < imgs.length ? i : imgs.length - 1];
      step._frJrKind = 'step';
      io.observe(step);
    });
    return true;
  }

  /* --- 3. Número que cuenta -------------------------------------------------
     Muta nodeValue, no textContent. Al terminar restituye el texto original
     exacto, así se conserva el formato que escribió el autor ("1.200", "10 años"). */

  function initStat(el, io) {
    var target = el.querySelector('.fr-jr-n') || el.querySelector('b');
    if (!target) return false;

    var node = target.firstChild;
    if (!node || node.nodeType !== 3) return false;

    var to = parseFloat(String(el.getAttribute('data-fr-to') || '').replace(/[^\d.-]/g, ''));
    // Sin data-fr-to (o si el editor se lo comió) queda el número final. Default seguro.
    if (!isFinite(to)) return false;

    el._frJrNode = node;
    el._frJrFinal = node.nodeValue;
    el._frJrTo = to;
    el._frJrKind = 'stat';

    io.observe(el);
    return true;
  }

  function runCount(el) {
    var node = el._frJrNode;
    var to = el._frJrTo;
    var final = el._frJrFinal;
    var dur = 1200;
    var start = 0;

    function frame(now) {
      if (!start) start = now;
      var k = clamp01((now - start) / dur);
      if (k >= 1) {
        node.nodeValue = final;
        return;
      }
      var eased = 1 - Math.pow(1 - k, 3);
      node.nodeValue = Math.round(to * eased).toLocaleString('es-AR');
      requestAnimationFrame(frame);
    }

    node.nodeValue = '0';
    requestAnimationFrame(frame);
  }

  /* --- 4. Cita palabra por palabra -----------------------------------------
     Los spans se envuelven UNA sola vez acá; el escalonado lo hace el CSS con
     transition-delay. Se conservan los espacios como nodos de texto reales
     para no romper lectores de pantalla ni el copiar/pegar. */

  function wrapWords(scope) {
    var walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT, {
      acceptNode: function (n) {
        if (!n.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
        // el pie de la cita (autor) no se escalona
        var p = n.parentNode;
        while (p && p !== scope) {
          var t = p.nodeName;
          if (t === 'CITE' || t === 'FOOTER') return NodeFilter.FILTER_REJECT;
          p = p.parentNode;
        }
        return NodeFilter.FILTER_ACCEPT;
      }
    });

    var nodes = [];
    var n;
    while ((n = walker.nextNode())) nodes.push(n);

    var i = 0;
    nodes.forEach(function (textNode) {
      var frag = document.createDocumentFragment();
      textNode.nodeValue.split(/(\s+)/).forEach(function (part) {
        if (!part) return;
        if (/^\s+$/.test(part)) {
          frag.appendChild(document.createTextNode(part));
          return;
        }
        var s = document.createElement('span');
        s.className = 'fr-jr-w';
        s.style.setProperty('--i', i++);
        s.appendChild(document.createTextNode(part));
        frag.appendChild(s);
      });
      textNode.parentNode.replaceChild(frag, textNode);
    });

    return i > 0;
  }

  function initQuote(el, io) {
    if (!wrapWords(el)) return false;
    el._frJrKind = 'on';
    io.observe(el);
    return true;
  }

  /* --- 5. Banda: entrada suave --------------------------------------------- */

  function initBand(el, io) {
    el._frJrKind = 'on';
    io.observe(el);
    return true;
  }

  /* --- 6c. Sonido y subtítulos del video con texto al lado -----------------
     El navegador no deja arrancar un video con audio sin que alguien lo pida:
     arranca mudo y el botón es la única forma de encenderlo. Por eso los
     subtítulos: la mayoría lo va a ver en silencio.

     El botón y la línea de subtítulos las crea el JS, no se escriben en la
     nota: el editor de artículos de Shopify limpia el HTML al guardar y se
     come los <button> y los data-*. Lo único que se pega es el <video> y,
     si hay subtítulos, un <a class="fr-jr-vt__vtt"> con el link al archivo
     — un enlace común sobrevive a cualquier limpieza.

     Los subtítulos los dibujamos nosotros y no el navegador, que los muestra
     a su manera y no se pueden estilar. Se escribe nodeValue y no textContent:
     el header del sitio vigila el body entero y cada nodo nuevo le dispara un
     recálculo completo. */

  function initVideoTexto(body) {
    all(body, '.fr-jr-vt__media').forEach(function (media) {
      var v = media.querySelector('video');
      if (!v) return;

      // Con "menos movimiento" el clip no arranca solo: se le dan los
      // controles nativos para que lo maneje quien quiera verlo.
      if (REDUCE) {
        v.setAttribute('controls', '');
        return;
      }

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'fr-jr-vt__son';
      btn.setAttribute('aria-pressed', 'false');
      btn.innerHTML =
        '<span class="fr-jr-vt__son-off">Sonido</span>' +
        '<span class="fr-jr-vt__son-on">Silenciar</span>';
      btn.addEventListener('click', function () {
        var prender = v.muted;
        v.muted = !prender;
        btn.setAttribute('aria-pressed', prender ? 'true' : 'false');
        if (prender && v.paused) {
          var pr = v.play();
          if (pr && pr.catch) pr.catch(function () {});
        }
      });
      media.appendChild(btn);

      // Subtítulos: el link se convierte en pista y desaparece de la vista.
      var enlace = media.querySelector('a.fr-jr-vt__vtt');
      if (!enlace || !enlace.getAttribute('href')) return;

      var pista = document.createElement('track');
      pista.kind = 'subtitles';
      pista.srclang = 'es';
      pista.label = 'Español';
      pista.src = enlace.getAttribute('href');
      pista.default = true;
      v.appendChild(pista);
      enlace.parentNode.removeChild(enlace);

      var cc = document.createElement('p');
      cc.className = 'fr-jr-vt__cc';
      var nodo = document.createTextNode('');
      cc.appendChild(nodo);
      media.appendChild(cc);

      var track = v.textTracks[v.textTracks.length - 1];
      track.mode = 'hidden'; // los dibujamos nosotros
      track.addEventListener('cuechange', function () {
        var cues = track.activeCues;
        nodo.nodeValue = cues && cues.length ? cues[0].text : '';
      });
    });
  }

  /* --- 6. Imágenes del cuerpo ----------------------------------------------
     No se toca src: el parser ya lanzó los fetch y reescribirlo genera un
     segundo request sin cancelar el primero. Solo hints. */

  function hintImages(body) {
    all(body, 'img').forEach(function (img, i) {
      img.setAttribute('decoding', 'async');
      if (i > 0 && !img.hasAttribute('loading')) img.setAttribute('loading', 'lazy');
    });
  }

  /* --- 7b. Cuenta atrás ------------------------------------------------------
     Muta nodeValue igual que el contador. Sin JS queda la fecha escrita a mano,
     que ya sirve. Si la fecha ya pasó, no toca nada. */

  function initCountdown(el) {
    var n = el.querySelector('.fr-jr-countdown__n');
    var u = el.querySelector('.fr-jr-countdown__u');
    if (!n || !n.firstChild || n.firstChild.nodeType !== 3) return;

    var iso = el.getAttribute('data-fr-jr-date');
    if (!iso) return;

    var target = new Date(iso + 'T00:00:00');
    if (isNaN(target)) return;

    var hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    var dias = Math.round((target - hoy) / 86400000);
    if (dias <= 0) return;

    n.firstChild.nodeValue = String(dias);
    if (u && u.firstChild && u.firstChild.nodeType === 3) {
      u.firstChild.nodeValue = dias === 1 ? 'día' : 'días';
    }
  }

  /* --- 6b. Clips de video ------------------------------------------------
     Solo corren mientras se ven. Con prefers-reduced-motion no arrancan:
     queda el primer cuadro, que es lo que espera quien pidió menos movimiento. */

  function initClips(body) {
    var clips = all(body, 'video.fr-jr-clip, .fr-jr-clips video, .fr-jr-vt__media video');
    if (!clips.length) return;

    clips.forEach(function (v) {
      v.muted = true;
      v.loop = true;
      v.playsInline = true;
      v.setAttribute('playsinline', '');
      v.setAttribute('preload', 'metadata');
      v.removeAttribute('controls');
    });

    if (REDUCE) return;

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          var v = e.target;
          if (e.isIntersecting) {
            var p = v.play();
            if (p && p.catch) p.catch(function () {});
          } else if (!v.paused) {
            v.pause();
          }
        });
      },
      { threshold: 0.25 }
    );
    clips.forEach(function (v) { io.observe(v); });
  }

  /* --- 7. Capitular ---------------------------------------------------------
     No se puede hacer solo con CSS: `p:first-of-type` agarra el primer <p> del
     cuerpo, que casi siempre es un módulo (.fr-jr-num) — y le partía el "01"
     en un 0 gigante y un 1 chico. Acá se elige el primer párrafo de texto de
     verdad: sin clase, sin marcadores y con largo suficiente.
     Es puro adorno: sin JS la nota se lee igual, solo sin capitular. */

  function dropCap(body) {
    var ps = all(body, ':scope > p');
    for (var i = 0; i < ps.length; i++) {
      var p = ps[i];
      if (p.className) continue;
      if (p.querySelector('img')) continue;
      var t = (p.textContent || '').trim();
      if (t.length < 80) continue;
      if (!/^[A-Za-zÁÉÍÓÚÑÜáéíóúñü¿¡"“(]/.test(t)) continue;
      p.classList.add('fr-jr-drop');
      return;
    }
  }

  /* --- init ---------------------------------------------------------------- */

  function init(root) {
    if (!root || root._frJrInit) return;
    root._frJrInit = true;

    if (REDUCE || !('IntersectionObserver' in window)) return;

    var body = root.querySelector('[data-fr-jr-body]');
    if (body) {
      hintImages(body);
      dropCap(body);
      initClips(body);
    }

    // Observer del centro de pantalla: define qué paso del scrolly manda.
    var ioCenter = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting && e.target._frJrKind === 'step') {
            showImage(e.target._frJrImg);
            markStep(e.target);
          }
        });
      },
      { threshold: 0, rootMargin: '-35% 0px -45% 0px' }
    );

    // Observer de entrada: cosas que se prenden una vez y no se apagan.
    var ioEnter = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          var el = e.target;
          obs.unobserve(el);
          if (el._frJrKind === 'stat') runCount(el);
          else el.classList.add('is-on');
        });
      },
      { threshold: 0.25 }
    );

    if (body) {
      all(body, '.fr-jr-scrolly').forEach(function (el) {
        initScrolly(el, ioCenter);
      });
      all(body, '.fr-jr-stat').forEach(function (el) {
        initStat(el, ioEnter);
      });
      all(body, '.fr-jr-quote').forEach(function (el) {
        initQuote(el, ioEnter);
      });
      all(body, '.fr-jr-band').forEach(function (el) {
        initBand(el, ioEnter);
      });
      // Reescribe: misma mecánica que la banda — se prende una vez al entrar.
      all(body, '.fr-jr-reescribe').forEach(function (el) {
        initBand(el, ioEnter);
      });
      all(body, '.fr-jr-countdown').forEach(initCountdown);
      initVideoTexto(body);
    }

    // Gate al final: todo lo de arriba ya dejó el DOM en un estado consistente.
    document.documentElement.setAttribute('data-fr-jr-ready', '');

    if (body) initProgress(body);
  }

  function boot() {
    init(document.querySelector('[data-fr-jr-root]'));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // El customizer re-renderiza la sección entera: hay que rehidratar.
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector('[data-fr-jr-root]');
    if (el) init(el);
  });
})();
